import React, { useState, useEffect, useCallback, useMemo } from "react";
import { makeStyles } from "@mui/styles";
import {
  Container,
  Typography,
  TextField,
  InputAdornment,
  Chip,
  IconButton,
  Box,
  Card,
  Grid,
  Button,
} from "@mui/material";
import {
  Download,
  Refresh,
  Search,
  Star,
  TrendingUp as TrendingUpIcon,
  TrendingDown,
} from "@mui/icons-material";
import { motion } from 'framer-motion';
import { CryptoState } from "../CryptoContext";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import { CoinList, GlobalData } from "../config/api";
import { fetchWithRetry, handleApiError } from "../utils/apiUtils";
import SparklineChart from "./SparklineChart";
import {
  calculateRSI,
  calculateMACD,
  calculateSMA,
  calculateVolatility,
  calculateATHDistance,
  formatLargeNumber,
  calculateVolumeMarketCapRatio,
  calculateCirculatingSupplyRatio,
} from "../utils/tradingUtils";
import { colors, typography, spacing, borderRadius, transitions, componentStyles } from "../styles/designSystem";

export function numberWithCommas(x) {
  return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

const useStyles = makeStyles((theme) => ({
  container: {
    background: colors.gradients.hero,
    minHeight: "100vh",
    padding: `${spacing['4xl']} 0`,
    position: 'relative',
    overflow: 'hidden',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'radial-gradient(circle at 20% 80%, rgba(230, 0, 122, 0.1) 0%, transparent 50%)',
      pointerEvents: 'none',
    },
  },
  
  sectionContainer: {
    ...componentStyles.contentContainer,
    position: 'relative',
    zIndex: 2,
  },
  
  header: {
    textAlign: "center",
    marginBottom: spacing['3xl'],
    [theme.breakpoints.up('xl')]: {
      marginBottom: spacing['4xl'],
    },
  },
  
  title: {
    fontFamily: typography.fontFamily.primary,
    fontWeight: typography.fontWeight.black,
    fontSize: typography.fontSize['4xl'],
    color: colors.text.primary,
    marginBottom: spacing.md,
    letterSpacing: typography.letterSpacing.tight,
    background: colors.gradients.primary,
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
    [theme.breakpoints.up('xl')]: {
      fontSize: typography.fontSize['5xl'],
    },
    [theme.breakpoints.up('xxl')]: {
      fontSize: typography.fontSize['6xl'],
    },
  },
  
  subtitle: {
    fontFamily: typography.fontFamily.secondary,
    fontWeight: typography.fontWeight.regular,
    fontSize: typography.fontSize.lg,
    color: colors.text.secondary,
    maxWidth: '600px',
    margin: '0 auto',
    lineHeight: typography.lineHeight.relaxed,
    letterSpacing: typography.letterSpacing.normal,
    [theme.breakpoints.up('xl')]: {
      fontSize: typography.fontSize.xl,
      maxWidth: '800px',
    },
    [theme.breakpoints.up('xxl')]: {
      fontSize: typography.fontSize['2xl'],
      maxWidth: '1000px',
    },
  },
  
  controlsCard: {
    ...componentStyles.glassCard,
    marginBottom: spacing['2xl'],
    padding: spacing.xl,
  },
  
  searchContainer: {
    marginBottom: spacing.lg,
    '& .MuiTextField-root': {
      background: colors.gradients.glass,
      borderRadius: borderRadius.xl,
      backdropFilter: 'blur(10px)',
      '& .MuiOutlinedInput-root': {
        color: colors.text.primary,
        fontFamily: typography.fontFamily.secondary,
        fontWeight: typography.fontWeight.medium,
        '& fieldset': {
          borderColor: 'rgba(255, 255, 255, 0.1)',
        },
        '&:hover fieldset': {
          borderColor: colors.primary.light,
        },
        '&.Mui-focused fieldset': {
          borderColor: colors.primary.main,
        },
      },
    },
  },
  
  filterContainer: {
    display: 'flex',
    gap: spacing.lg,
    flexWrap: 'wrap',
    alignItems: 'center',
  },
  
  filterChip: {
    background: colors.gradients.glass,
    border: `1px solid rgba(255, 255, 255, 0.1)`,
    color: colors.text.primary,
    fontFamily: typography.fontFamily.secondary,
    fontWeight: typography.fontWeight.medium,
    fontSize: typography.fontSize.base,
    padding: `${spacing.sm} ${spacing.md}`,
    borderRadius: borderRadius.xl,
    backdropFilter: 'blur(10px)',
    transition: transitions.smooth,
    cursor: 'pointer',
    '&:hover': {
      background: 'rgba(230, 0, 122, 0.2)',
      border: `1px solid ${colors.primary.main}`,
      transform: 'translateY(-2px)',
    },
    '&.active': {
      background: colors.primary.main,
      border: `1px solid ${colors.primary.main}`,
      color: colors.text.primary,
    },
  },
  
  actionButton: {
    ...componentStyles.button.primary,
    fontSize: typography.fontSize.base,
    padding: `${spacing.sm} ${spacing.lg}`,
    borderRadius: borderRadius.xl,
    minWidth: 'auto',
  },
  
  gridContainer: {
    height: "700px",
    background: colors.gradients.glass,
    borderRadius: borderRadius['2xl'],
    border: `1px solid rgba(255, 255, 255, 0.1)`,
    backdropFilter: 'blur(20px)',
    overflow: 'hidden',
    '& .ag-theme-alpine-dark': {
      background: 'transparent',
      '& .ag-header': {
        background: 'rgba(255, 255, 255, 0.05)',
        color: colors.text.primary,
        borderBottom: `1px solid rgba(255, 255, 255, 0.1)`,
        backdropFilter: 'blur(10px)',
      },
      '& .ag-header-cell': {
        fontFamily: typography.fontFamily.primary,
        fontWeight: typography.fontWeight.semiBold,
        fontSize: typography.fontSize.base,
        color: colors.text.primary,
        borderRight: `1px solid rgba(255, 255, 255, 0.1)`,
      },
      '& .ag-row': {
        background: 'transparent',
        borderBottom: `1px solid rgba(255, 255, 255, 0.05)`,
        transition: transitions.smooth,
        '&:hover': {
          background: 'rgba(230, 0, 122, 0.1)',
        },
      },
      '& .ag-cell': {
        fontFamily: typography.fontFamily.secondary,
        fontWeight: typography.fontWeight.medium,
        fontSize: typography.fontSize.base,
        color: colors.text.primary,
        borderRight: `1px solid rgba(255, 255, 255, 0.05)`,
        padding: spacing.md,
      },
      '& .ag-paging-panel': {
        background: 'rgba(255, 255, 255, 0.05)',
        borderTop: `1px solid rgba(255, 255, 255, 0.1)`,
        color: colors.text.primary,
        backdropFilter: 'blur(10px)',
      },
    },
  },
  
  statsGrid: {
    marginBottom: spacing['3xl'],
  },
  
  statCard: {
    ...componentStyles.glassCard,
    textAlign: 'center',
    padding: spacing.xl,
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
  },
  
  statNumber: {
    fontFamily: typography.fontFamily.primary,
    fontWeight: typography.fontWeight.black,
    fontSize: typography.fontSize['3xl'],
    color: colors.primary.main,
    marginBottom: spacing.sm,
    letterSpacing: typography.letterSpacing.tight,
  },
  
  statLabel: {
    fontFamily: typography.fontFamily.secondary,
    fontWeight: typography.fontWeight.medium,
    fontSize: typography.fontSize.base,
    color: colors.text.secondary,
    textTransform: 'uppercase',
    letterSpacing: typography.letterSpacing.wider,
  },
  
  loadingCard: {
    ...componentStyles.glassCard,
    textAlign: 'center',
    padding: spacing['3xl'],
  },
  
  errorCard: {
    ...componentStyles.glassCard,
    textAlign: 'center',
    padding: spacing['3xl'],
    border: `1px solid ${colors.error.main}`,
  },
}));

export default function CoinsTable({ onCoinSelect }) {
  const classes = useStyles();
  const { currency, symbol } = CryptoState();
  const [coins, setCoins] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("market_cap");
  const [sortOrder] = useState("desc");
  const [globalData, setGlobalData] = useState(null);
  const [favorites, setFavorites] = useState([]);
  const [pageSize] = useState(20);

  const fetchCoins = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetchWithRetry(CoinList(currency));
      const coinData = response.data.map((coin) => ({
        ...coin,
        sparkline_in_7d: coin.sparkline_in_7d,
        rsi: calculateRSI(coin.sparkline_in_7d?.price || []),
        macd: calculateMACD(coin.sparkline_in_7d?.price || []),
        sma: calculateSMA(coin.sparkline_in_7d?.price || [], 20),
        volatility: calculateVolatility(coin.sparkline_in_7d?.price || []),
        ath_distance: calculateATHDistance(coin.current_price, coin.ath),
        volume_market_cap_ratio: calculateVolumeMarketCapRatio(coin.total_volume, coin.market_cap),
        circulating_supply_ratio: calculateCirculatingSupplyRatio(coin.circulating_supply, coin.total_supply),
      }));
      setCoins(coinData);
    } catch (err) {
      const errorInfo = handleApiError(err, 'Coin list fetch');
      setError("Failed to fetch coin data. Please try again.");
      console.error("Error fetching coins:", errorInfo);
    } finally {
      setLoading(false);
    }
  }, [currency]);

  const fetchGlobalData = useCallback(async () => {
    try {
      const response = await fetchWithRetry(GlobalData());
      setGlobalData(response.data);
    } catch (err) {
      const errorInfo = handleApiError(err, 'Global data fetch');
      console.error("Error fetching global data:", errorInfo);
    }
  }, []);

  useEffect(() => {
    fetchCoins();
    fetchGlobalData();
  }, [fetchCoins, fetchGlobalData]);

  const filteredCoins = useMemo(() => {
    return coins.filter((coin) =>
      coin.name.toLowerCase().includes(search.toLowerCase()) ||
      coin.symbol.toLowerCase().includes(search.toLowerCase())
    );
  }, [coins, search]);

  const sortedCoins = useMemo(() => {
    return [...filteredCoins].sort((a, b) => {
      let aValue = a[sortBy];
      let bValue = b[sortBy];
      
      if (sortBy === "price_change_percentage_24h") {
        aValue = a.price_change_percentage_24h || 0;
        bValue = b.price_change_percentage_24h || 0;
      }
      
      if (sortOrder === "asc") {
        return aValue - bValue;
      } else {
        return bValue - aValue;
      }
    });
  }, [filteredCoins, sortBy, sortOrder]);

  const handleRefresh = () => {
    fetchCoins();
    fetchGlobalData();
  };

  const handleExport = () => {
    const csvContent = "data:text/csv;charset=utf-8," + 
      "Name,Symbol,Price,Market Cap,24h Change,Volume\n" +
      sortedCoins.map(coin => 
        `${coin.name},${coin.symbol},${coin.current_price},${coin.market_cap},${coin.price_change_percentage_24h},${coin.total_volume}`
      ).join("\n");
    
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", "crypto_data.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const toggleFavorite = (coinId) => {
    setFavorites(prev => 
      prev.includes(coinId) 
        ? prev.filter(id => id !== coinId)
        : [...prev, coinId]
    );
  };

  const containerVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        staggerChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: 'easeOut',
      },
    },
  };

  const columnDefs = [
    {
      headerName: "Rank",
      field: "market_cap_rank",
      width: 80,
      cellRenderer: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton
            size="small"
            onClick={() => toggleFavorite(params.data.id)}
            sx={{ 
              color: favorites.includes(params.data.id) ? colors.primary.main : colors.text.secondary,
              '&:hover': { color: colors.primary.main }
            }}
          >
            <Star fontSize="small" />
          </IconButton>
          {params.value}
        </Box>
      ),
    },
    {
      headerName: "Coin",
      field: "name",
      width: 200,
             cellRenderer: (params) => (
         <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, cursor: 'pointer' }}
              onClick={() => {
                if (onCoinSelect) onCoinSelect(params.data);
              }}>
           <img src={params.data.image} alt={params.data.name} style={{ width: 30, height: 30 }} />
           <Box>
             <Typography variant="body2" sx={{ fontWeight: 'bold', color: colors.text.primary }}>
               {params.data.name}
             </Typography>
             <Typography variant="caption" sx={{ color: colors.text.secondary }}>
               {params.data.symbol.toUpperCase()}
             </Typography>
           </Box>
         </Box>
       ),
    },
    {
      headerName: "Price",
      field: "current_price",
      width: 120,
      cellRenderer: (params) => (
        <Typography variant="body2" sx={{ color: colors.text.primary }}>
          {symbol} {numberWithCommas(params.value?.toFixed(2) || "0.00")}
        </Typography>
      ),
    },
    {
      headerName: "24h Change",
      field: "price_change_percentage_24h",
      width: 120,
      cellRenderer: (params) => {
        const value = params.value || 0;
        const color = value >= 0 ? colors.success.main : colors.error.main;
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {value >= 0 ? <TrendingUpIcon fontSize="small" /> : <TrendingDown fontSize="small" />}
            <Typography variant="body2" sx={{ color }}>
              {value?.toFixed(2)}%
            </Typography>
          </Box>
        );
      },
    },
    {
      headerName: "Market Cap",
      field: "market_cap",
      width: 150,
      cellRenderer: (params) => (
        <Typography variant="body2" sx={{ color: colors.text.primary }}>
          {symbol} {formatLargeNumber(params.value)}
        </Typography>
      ),
    },
    {
      headerName: "Volume",
      field: "total_volume",
      width: 150,
      cellRenderer: (params) => (
        <Typography variant="body2" sx={{ color: colors.text.primary }}>
          {symbol} {formatLargeNumber(params.value)}
        </Typography>
      ),
    },
    {
      headerName: "Sparkline",
      field: "sparkline_in_7d",
      width: 200,
      cellRenderer: (params) => (
        <Box sx={{ width: '100%', height: 40 }}>
          <SparklineChart data={params.value?.price || []} />
        </Box>
      ),
    },
  ];

  if (loading) {
    return (
      <Box className={classes.container}>
        <Container className={classes.sectionContainer}>
          <motion.div
            initial="hidden"
            animate="visible"
            variants={containerVariants}
          >
            <motion.div variants={itemVariants}>
              <Card className={classes.loadingCard}>
                <Typography variant="h5" sx={{ color: colors.text.primary, marginBottom: 2 }}>
                  Loading Market Data...
                </Typography>
                <Typography variant="body1" sx={{ color: colors.text.secondary }}>
                  Please wait while we fetch the latest cryptocurrency data.
                </Typography>
              </Card>
            </motion.div>
          </motion.div>
        </Container>
      </Box>
    );
  }

  if (error) {
    return (
      <Box className={classes.container}>
        <Container className={classes.sectionContainer}>
          <motion.div
            initial="hidden"
            animate="visible"
            variants={containerVariants}
          >
            <motion.div variants={itemVariants}>
              <Card className={classes.errorCard}>
                <Typography variant="h5" sx={{ color: colors.error.main, marginBottom: 2 }}>
                  Error Loading Data
                </Typography>
                <Typography variant="body1" sx={{ color: colors.text.secondary, marginBottom: 2 }}>
                  {error}
                </Typography>
                <Button
                  variant="contained"
                  onClick={handleRefresh}
                  className={classes.actionButton}
                  startIcon={<Refresh />}
                >
                  Try Again
                </Button>
              </Card>
            </motion.div>
          </motion.div>
        </Container>
      </Box>
    );
  }

  return (
    <Box className={classes.container}>
      <Container className={classes.sectionContainer}>
        <motion.div
          initial="hidden"
          animate="visible"
          variants={containerVariants}
        >
          <motion.div variants={itemVariants}>
            <Box className={classes.header}>
              <Typography className={classes.title}>
                Cryptocurrency Market
              </Typography>
              <Typography className={classes.subtitle}>
                Track real-time cryptocurrency prices, market caps, and trading volumes. 
                Stay informed with our comprehensive market data and advanced analytics.
              </Typography>
            </Box>
          </motion.div>

          {globalData && (
            <motion.div variants={itemVariants}>
              <Grid container spacing={3} className={classes.statsGrid}>
                <Grid item xs={12} sm={6} md={3}>
                  <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                    <Card className={classes.statCard}>
                      <Typography className={classes.statNumber}>
                        {symbol} {formatLargeNumber(globalData.total_market_cap?.[currency] || 0)}
                      </Typography>
                      <Typography className={classes.statLabel}>
                        Total Market Cap
                      </Typography>
                    </Card>
                  </motion.div>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                    <Card className={classes.statCard}>
                      <Typography className={classes.statNumber}>
                        {symbol} {formatLargeNumber(globalData.total_volume?.[currency] || 0)}
                      </Typography>
                      <Typography className={classes.statLabel}>
                        24h Volume
                      </Typography>
                    </Card>
                  </motion.div>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                    <Card className={classes.statCard}>
                      <Typography className={classes.statNumber}>
                        {globalData.active_cryptocurrencies || 0}
                      </Typography>
                      <Typography className={classes.statLabel}>
                        Active Coins
                      </Typography>
                    </Card>
                  </motion.div>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                    <Card className={classes.statCard}>
                      <Typography className={classes.statNumber}>
                        {globalData.market_cap_change_percentage_24h?.toFixed(2) || '0.00'}%
                      </Typography>
                      <Typography className={classes.statLabel}>
                        Market Change
                      </Typography>
                    </Card>
                  </motion.div>
                </Grid>
              </Grid>
            </motion.div>
          )}

          <motion.div variants={itemVariants}>
            <Card className={classes.controlsCard}>
              <Grid container spacing={3} alignItems="center">
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    placeholder="Search for a cryptocurrency..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className={classes.searchContainer}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search sx={{ color: colors.text.secondary }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box className={classes.filterContainer}>
                    <Chip
                      label="Market Cap"
                      className={`${classes.filterChip} ${sortBy === 'market_cap' ? 'active' : ''}`}
                      onClick={() => setSortBy('market_cap')}
                    />
                    <Chip
                      label="Price Change"
                      className={`${classes.filterChip} ${sortBy === 'price_change_percentage_24h' ? 'active' : ''}`}
                      onClick={() => setSortBy('price_change_percentage_24h')}
                    />
                    <Chip
                      label="Volume"
                      className={`${classes.filterChip} ${sortBy === 'total_volume' ? 'active' : ''}`}
                      onClick={() => setSortBy('total_volume')}
                    />
                    <IconButton
                      onClick={handleRefresh}
                      className={classes.actionButton}
                      size="small"
                    >
                      <Refresh />
                    </IconButton>
                    <IconButton
                      onClick={handleExport}
                      className={classes.actionButton}
                      size="small"
                    >
                      <Download />
                    </IconButton>
                  </Box>
                </Grid>
              </Grid>
            </Card>
          </motion.div>

          <motion.div variants={itemVariants}>
            <Box className={classes.gridContainer}>
              <AgGridReact
                columnDefs={columnDefs}
                rowData={sortedCoins}
                pagination={true}
                paginationPageSize={pageSize}
                onPaginationChanged={(event) => {
                  // Page tracking removed as it wasn't being used
                }}
                domLayout="autoHeight"
                suppressRowClickSelection={true}
                suppressCellFocus={true}
                suppressRowHoverHighlight={false}
                rowHeight={60}
                headerHeight={50}
                defaultColDef={{
                  sortable: true,
                  resizable: true,
                  filter: true,
                }}
              />
            </Box>
          </motion.div>
        </motion.div>
      </Container>
    </Box>
  );
}
